import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBar, ChatCircle, ShareNetwork, Brain, Check } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';

import { Badge, Spinner } from '@/components/ui';
import { useSharePoll } from '@/hooks';
import { useCastVote } from '@/api';
import { useAuthStore } from '@/stores';

import type { Poll, Option } from '@/types/poll';

import styles from './PollCard.module.css';

const OPTION_COLORS = [
    '#6366F1',
    '#06B6D4',
    '#10B981',
    '#F59E0B',
    '#EC4899',
    '#EF4444',
];

interface PollCardProps {
    poll: Poll;
}

function getPercentage(option: Option, totalVotes: number): number {
    if (totalVotes === 0) return 0;
    return Math.round((option.vote_count / totalVotes) * 100);
}

export function PollCard({ poll }: PollCardProps) {
    const navigate = useNavigate();
    const { share } = useSharePoll();
    const { isLogged } = useAuthStore();
    const castVote = useCastVote(poll.slug);
    const cardRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);

    const [expanded, setExpanded] = useState(false);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);

    // Close when clicking anywhere outside this card
    useEffect(() => {
        if (!expanded) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
                setExpanded(false);
                setSelectedOption(null);
            }
        };
        document.addEventListener('click', handleOutsideClick, true);
        return () => document.removeEventListener('click', handleOutsideClick, true);
    }, [expanded]);

    const hasVoted = poll.user_vote !== null && poll.user_vote !== undefined && poll.user_vote.length > 0;
    const canVote = isLogged && !hasVoted && poll.is_active;
    const isSingleChoice = poll.poll_type === 'single_choice';

    const timeAgo = formatDistanceToNow(new Date(poll.created), {
        addSuffix: false,
        locale: es,
    });

    const handleShare = (e: React.MouseEvent) => {
        e.stopPropagation();
        share(poll.author.slug, poll.slug, poll.title);
    };

    const handleCardClick = (e: React.MouseEvent) => {
        if (optionsRef.current?.contains(e.target as Node)) return;
        if (actionsRef.current?.contains(e.target as Node)) return;
        if (expanded) {
            setExpanded(false);
            setSelectedOption(null);
            return;
        }
        navigate(`/${poll.author.slug}/${poll.slug}`);
    };

    const handleOptionsClick = () => {
        if (!expanded) {
            setExpanded(true);
        }
    };

    const handleSelectOption = (optionId: number) => {
        if (!expanded || !canVote) return;
        if (isSingleChoice) {
            setSelectedOption(optionId);
        } else {
            setSelectedOption((prev) => (prev === optionId ? null : optionId));
        }
    };

    const handleVote = () => {
        if (!selectedOption || castVote.isPending) return;
        castVote.mutate(
            { option_ids: [selectedOption] },
            { onSuccess: () => setExpanded(false) },
        );
    };

    const displayOptions = expanded ? poll.options : poll.options.slice(0, 3);
    const remainingCount = expanded ? 0 : poll.options.length - 3;

    return (
        <div ref={cardRef} className={styles.card} onClick={handleCardClick} role="link" tabIndex={0}>
            {/* Header */}
            <div className={styles.header}>
                <span className={styles.time}>{timeAgo}</span>
                <div className={styles.badges}>
                    <Badge variant={poll.is_quiz ? 'info' : 'default'}>
                        {poll.is_quiz ? 'Trivia' : 'Encuesta'}
                    </Badge>
                    {!poll.is_active && (
                        <Badge variant="error">Cerrada</Badge>
                    )}
                    {hasVoted && (
                        <Badge variant="success">Votado</Badge>
                    )}
                </div>
            </div>

            {/* Title */}
            <h3 className={styles.title}>
                {poll.is_quiz && <Brain size={14} weight="bold" className={styles.quizIcon} />}
                {poll.title}
            </h3>

            {/* Description */}
            {poll.description && (
                <p className={styles.description}>{poll.description}</p>
            )}

            {/* Options */}
            <div
                ref={optionsRef}
                className={`${styles.optionsPreview} ${!expanded && !hasVoted ? styles.optionsClickable : ''} ${expanded ? styles.optionsExpanded : ''}`}
                onClick={handleOptionsClick}
            >
                {castVote.isPending ? (
                    <div className={styles.votingOverlay}>
                        <Spinner size="sm" />
                        <span className={styles.votingText}>Votando...</span>
                    </div>
                ) : (
                    <>
                        <AnimatePresence mode="popLayout">
                            {displayOptions.map((option, index) => {
                                const color = OPTION_COLORS[index % OPTION_COLORS.length];
                                const pct = getPercentage(option, poll.total_votes);
                                const isMyVote = hasVoted && poll.user_vote?.includes(option.id);
                                const isSelected = selectedOption === option.id;
                                const showResults = expanded && (!canVote || hasVoted || !poll.is_active);

                                return (
                                    <motion.div
                                        key={option.id}
                                        className={`${styles.optionBar} ${expanded && canVote ? styles.optionSelectable : ''} ${isSelected ? styles.optionSelected : ''} ${isMyVote ? styles.optionMyVote : ''}`}
                                        onClick={expanded && canVote ? (e) => { e.stopPropagation(); handleSelectOption(option.id); } : undefined}
                                        initial={index >= 3 ? { opacity: 0, height: 0 } : false}
                                        animate={{ opacity: 1, height: 28 }}
                                        transition={{ duration: 0.15, delay: index >= 3 ? (index - 3) * 0.05 : 0 }}
                                    >
                                        {(hasVoted || showResults) && (
                                            <div
                                                className={styles.optionFill}
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: `${color}20`,
                                                }}
                                            />
                                        )}
                                        <div
                                            className={styles.optionAccent}
                                            style={{ backgroundColor: color }}
                                        />
                                        <span className={styles.optionText}>
                                            {option.text.length > 50 ? `${option.text.slice(0, 50)}...` : option.text}
                                        </span>
                                        {isSelected && (
                                            <span className={styles.optionCheck}>
                                                <Check size={12} weight="bold" />
                                            </span>
                                        )}
                                        {(hasVoted || showResults) && (
                                            <span className={styles.optionPct}>{pct}%</span>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>

                        {remainingCount > 0 && (
                            <span className={styles.moreOptions}>+{remainingCount} mas</span>
                        )}

                        {!expanded && canVote && (
                            <span className={styles.tapHint}>Toca para votar</span>
                        )}

                        {!expanded && !canVote && !hasVoted && (
                            <span className={styles.tapHint}>Toca para ver resultados</span>
                        )}

                        {expanded && !poll.is_active && (
                            <span className={styles.closedHint}>Encuesta cerrada</span>
                        )}

                        {expanded && canVote && (
                            <motion.button
                                className={styles.inlineVoteBtn}
                                onClick={(e) => { e.stopPropagation(); handleVote(); }}
                                disabled={!selectedOption || castVote.isPending}
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.15, delay: 0.1 }}
                                whileTap={{ scale: 0.97 }}
                                type="button"
                            >
                                Votar
                            </motion.button>
                        )}
                    </>
                )}
            </div>

            {/* Action bar */}
            <div ref={actionsRef} className={styles.actionBar}>
                <span className={styles.authorName}>{poll.author.display_name}</span>
                <span className={styles.dot}>&middot;</span>
                <div className={styles.action}>
                    <ChartBar size={14} />
                    <span>{poll.total_votes}</span>
                </div>
                <div className={styles.action}>
                    <ChatCircle size={14} />
                    <span>{poll.total_comments}</span>
                </div>
                <button
                    className={styles.actionBtn}
                    onClick={handleShare}
                    aria-label="Compartir encuesta"
                    type="button"
                >
                    <ShareNetwork size={16} />
                    <span>Compartir</span>
                </button>
            </div>
        </div>
    );
}
